
/*
 * Date: 2025
 * Description: Users pages
 * Author: Philippe Leroux @ SKITSC
 */

//Modules
import { Box, Typography , Button, IconButton , Modal } from "@mui/material";
import { useEffect, useState , useContext, ReactElement } from "react";
import { useNavigate } from "react-router-dom";

//Interfaces
import { i_initial_props, i_table_props, i_tbl_header , i_alert , i_alert_control, i_prompt_modal_props } from "../../interfaces/utility.interface";
import { i_user, i_user_form } from "../../interfaces/user.interface";
import { t_display, t_transition_event } from "../../types/types";

//Constants
import { default_user , default_alert } from "../../utils/constant";

//Utilities
import { handleFetch } from "../../api/fetch";

//Context
import { MainContext, ThemeContext } from "../../context/context";

//Components
import Tbl from "../../components/table/main.table";
import UserForm from "../../components/form/user.form";
import AlertTop from "../../components/utils/alert";
import PromptBody from "../../components/modal/prompt.body";

//Styles
import { header_row } from "../../styles/tbl.styles";

//Icons
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

//Middlewares & validators
import { sanitizeUser } from "../../middlewares/main.middleware";
import { validateEmail, validateEmpty, validatePassword } from "../../middlewares/validator";



const Users = ( props : i_initial_props ) : ReactElement => {
    const { mode } = useContext(ThemeContext)
    const { HandleLogout } = useContext(MainContext);
    const [ display , setDisplay ] = useState<t_display>('table');
    const [ data , setData ] = useState<i_user[]>([]);
    const [ loading , setLoading ] = useState<boolean>(false);
    const [ form , setForm ] = useState<i_user>(default_user);
    const [ errors , setErrors ] = useState<i_user>(default_user);
    const [ alert , setAlert ] = useState<i_alert>(default_alert);
    const [ open , setOpen ] = useState<boolean>(false);
    const [ modal_type , setModalType ] = useState<t_transition_event>('disable')
    const nav = useNavigate();

    useEffect(() => {
        const getUsers = async() : Promise<void> => {
            setLoading(true);
            const res = await handleFetch('/users', 'GET', true, null);
            if(res.type === 'Success'){
                setData(res.data);
            }
            if(res.type === 'Unauthorized'){
                HandleLogout(nav);
            }
            setLoading(false);
        }
        getUsers();
    },[])
    useEffect(() => {
        if(errors.username !== '' && validateEmpty(form.username)) setErrors({...errors, username : '' })
        if(errors.email !== '' && validateEmail(form.email)) setErrors({...errors, email : '' })
        if(errors.password !== '' && validatePassword(form.password)) setErrors({...errors, password : '' })
    },[form , errors])

    const handleRow = async( row : i_user, action : t_display) => {
        if(action === 'form'){
            setForm({...row , password : '' })
            setDisplay('form')
        }
        if(action === 'delete'){
            setForm(row)
            setModalType('delete')
            setOpen(true)
        }
        if(action === 'disable'){
            setForm(row)
            setModalType('disable')
            setOpen(true)
        }
    }

    const handleSubmit = async() => {
        const method = form._id !== '' ? 'PUT' : 'POST';
        const [ valid , field , msg ] = sanitizeUser(form ,method)
        if(valid){
            const res = await handleFetch('/user', method , true, form);
            if(res.type === 'Success'){
                if(method === 'POST')setData([...data, res.data])
                if(method === 'PUT') setData(data.map((item : i_user)=> item._id === form._id? form : item))
                setForm(default_user)
                setErrors(default_user)
                setDisplay('table')
                return
            }
            if(res.type === 'Unauthorized'){
                HandleLogout(nav);
                return
            }else{

                setAlert({open : true, message : res.message , severity: 'error'})
            }
        }else{
            console.log('Error in handleSubmit' , field, msg)
            setErrors({...errors, [field] : msg})
        }
    }
    const handleDelete = async( id : string) => {
        const res = await handleFetch('/user/'+id, 'DELETE', true, null);
        if(res.type === 'Success'){
            setData(data.filter(item => item._id!== id))
            setOpen(false)
            return
        }
        if(res.type === 'Unauthorized'){
            HandleLogout(nav);
            return
        }
        setAlert({open : true, message : res.message , severity: 'error'})
    }

    const handleActive = async( id : string ) => {
        const res = await handleFetch('/user/'+id, 'PATCH', true, null);
        if(res.type === 'Success'){
            setData(data.map(item => item._id === id? {...item, active : item.active === 1 ? 0 : 1 } : item))
            setOpen(false)
            return
        }
        if(res.type === 'Unauthorized'){
            HandleLogout(nav);
            return
        }
        setAlert({open : true, message : res.message , severity: 'error'})
    }
    const handleModalCallback = async() => {
        const _id = form._id ? form._id : ''
        if(modal_type === 'delete' && _id !== ''){
            handleDelete(_id)
        }
        if(modal_type === 'disable' && _id!== ''){
            handleActive(_id)
        }
    }
    const userHeaders : i_tbl_header[] = [
        { value : "Date added" , css : { ...header_row, } , portion : 3 , },
        { value : "Username" , css : { ...header_row } , portion : 3 },
        { value : "Email" , css : { ...header_row } , portion : 3 },
        { value : "Type" , css : { ...header_row } , portion : 1 },
        { value : 'Action', css : header_row, portion : 2  }
    ]
    const tbl_props : i_table_props = {
        data : data,
        title : 'Please start by adding a new user',
        loading : loading,
        headers : userHeaders,
        callback : handleRow,
        closeAlert : () => {},
        row_model : "user",
    }
    const handleFormTransition = () => {
        setForm(default_user)
        setDisplay('form')
    }
    const handleGoBack = () => {
        setDisplay('table')
        setForm(default_user)
    }
    const user_form_props : i_user_form = {
        form : form ,
        errors : errors,
        loading : loading,
        setErrors : setErrors,
        setForm : setForm,
        callback : handleSubmit,
    }
    const alert_props : i_alert_control = {
        alert : alert,
        onClose : () => setAlert(default_alert),
        float : 'top',
        top: 125
    }
    const interpreteActive = ( value : number ) => {
        return value === 1 ? "disable" : "enable"
    }
    const prompt_props : i_prompt_modal_props = {
        open : open,
        title :  modal_type === 'delete' ? "Delete user" : "Change " + form.username + ' to ' + interpreteActive(form.active),
        loading : loading,
        handleClose : () => setOpen(false),
        callback : handleModalCallback,
        type : modal_type,
        mode : mode
    }
    return (
        <Box>
            <Box sx={{ height : '100%'}}>
                <AlertTop {...alert_props}/>
                <Box sx={{ maxWidth : '1200px' , marginLeft : 'auto' , marginRight : 'auto'}}>
                <Typography sx={{ textAlign : 'center' , fontSize : 24 , fontWeight : 'bold' , paddingTop : "8vh"}}>
                    { display === 'table' ? "User management" : form._id === '' ?  "Add a new user !" : "Update this user" }
                </Typography>
                    { display === 'table' ? 
                        <Box>
                            <Button variant="contained" sx={{ margin : 2}} onClick={() => handleFormTransition()}>Add a new User</Button>
                            <Tbl {...tbl_props} />
                            {/* Table */}
                        </Box> 

                        : 
                        <Box>
                            <IconButton onClick={() => handleGoBack() }><ArrowBackIcon sx={{ fontSize : 50 }}/></IconButton>
                            <UserForm {...user_form_props  } />
                        </Box>
                    }
                </Box>
            </Box>
            <Modal open={open} onClose={() => setOpen(false)} children={<PromptBody {...prompt_props}/>} />
        </Box>
    )
}

export default Users;